import { memo } from 'react';
import { Chip, type IconPath } from '@nespresso-design-system/components';
import useTranslations from '@/widgets/hooks/useTranslations';
import type { Translations } from '@/widgets/ProductList/config';
import type { FiltersAndSort } from '@/widgets/services/FilteringAndSortingService';
import { trackFiltersApplied, trackFiltersRemoval } from '../../../Tracking.ts';
import styles from '../AllFiltersDrawer.module.scss';

type FilterListProps = {
  filtering: FiltersAndSort['filtering'];
};

type FilterOptionProps = {
  filtering: FiltersAndSort['filtering'];
  filterId: string;
  id: string;
  label: string;
  isSelected?: boolean;
  icon?: string;
};

const QUICK_FILTERS = 'QUICK';

const QuickFilterList = memo(({ filtering }: FilterListProps) => {
  const { t } = useTranslations<Translations>();
  const quickFilters = filtering.filters.value?.filter(
    (filter) => filter.type === QUICK_FILTERS
  );

  if (!quickFilters?.length) return null;

  return (
    <div class={styles.filterSection}>
      <h3 class={styles.filterTitle}>
        {t('productList.filters.quickFilters.title')}
      </h3>
      <ul class={styles.container}>
        {quickFilters.map((filter) => (
          <QuickFilter
            key={`${filter.options[0].id}-${filter.options[0].selected}`}
            filterId={filter.id}
            id={filter.options[0].id}
            label={filter.options[0].label}
            isSelected={filter.options[0].selected}
            icon={filter.options[0].icon}
            filtering={filtering}
          />
        ))}
      </ul>
    </div>
  );
});

export default QuickFilterList;

// Note: Is moved in a separate component to avoid re-rendering the entire drawer.
// We need to consume signals here NOT in a reactive way for this DS Chip.
const QuickFilter = memo(
  ({ filterId, id, label, isSelected, icon, filtering }: FilterOptionProps) => (
    <li>
      <Chip
        aria-label={label}
        label={label}
        isActive={isSelected}
        disabled={filtering.isDisabledFilterOption(filterId, id).value}
        onClick={() => {
          filtering.updateFilter(filterId, id, !isSelected);
          if (isSelected) {
            trackFiltersRemoval(
              filtering.currentFilters.value,
              'clear all chip'
            );
          } else {
            trackFiltersApplied(filtering.currentFilters.value);
          }
        }}
        icon={icon as IconPath}
      />
    </li>
  )
);
